import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { isBefore, formatISO, format, parseISO, isAfter } from 'date-fns';

import SaveButton from '@ui-elements/SaveButton';
import BasicModal from '@ui-parts/BasicModal';
import DatePicker from '@ui-parts/DatePicker';
import TimePicker from '@ui-parts/TimePicker';
import InputValidationErrorBox from '@ui-parts/InputValidationErrorBox';

import { propTypesNullable, extractDateFromIsoDate, addDaystoIsoDate } from '@functional';
import { PALETTE } from '~/assets/styles/base';
import { callThunk, updateSurvey } from '~/api';

const BodyWrapper = styled.div`
  padding: 0 15px;
`;

const DatePickerWrapper = styled.div`
  padding: 15px 0 35px;
  border-bottom: 1px solid ${PALETTE.border.modalItemBottom};
`;

const TimePickerWrapper = styled.div`
  padding: 35px 0 15px;
`;

const initialError = {
  invalid: false,
  message: '',
};

function DeliveryDateModal({
  open,
  handleClose,
  isoDeliveryDate,
  minDeliveryDate,
  forbiddenSchedules = [],
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [defExtractedDate, setDefExtractedDate] = useState(null);
  const [minDateStr, setMinDateStr] = useState(null);
  const [deliveryDateStr, setDeliveryDateStr] = useState(null);
  const [deliveryTimeStr, setDeliveryTimeStr] = useState(null);
  const [errors, setErrors] = useState(initialError);

  /**
   * =============
   * functions
   * =============
   */

  const initializeError = () => {
    setErrors(initialError);
  };

  const validate = () => {
    const selectedDateTime = parseISO(`${deliveryDateStr} ${deliveryTimeStr}`);
    if (isBefore(selectedDateTime, parseISO(minDeliveryDate))) {
      setErrors({
        invalid: true,
        message: t('validation.notAvailableDeliveryDate', {
          datetime: format(parseISO(minDeliveryDate), 'yyyy/MM/dd HH:mm'),
        }),
      });
      return false;
    }

    // メンテなどの配信禁止日の範囲に含まれないかチェック。
    let hasError = false;
    const forbiddenScheduleDivs = forbiddenSchedules
      .map((forbiddenSchedule) => {
        const startTime = format(forbiddenSchedule.startTime, 'yyyy/MM/dd HH:mm');
        const endTime = format(forbiddenSchedule.endTime, 'yyyy/MM/dd HH:mm');
        return `<div>・${startTime}~${endTime}</div>`;
      })
      .join('\n');

    // eslint-disable-next-line consistent-return
    forbiddenSchedules.forEach((forbiddenSchedule) => {
      if (
        isAfter(selectedDateTime, forbiddenSchedule.startTime) &&
        isBefore(selectedDateTime, forbiddenSchedule.endTime)
      ) {
        setErrors({
          invalid: true,
          message: t('validation.forbiddenDeliveryDate', {
            forbiddenSchedules: forbiddenScheduleDivs,
          }),
        });
        hasError = true;
      }
    });
    if (hasError) {
      return false;
    }
    initializeError();
    return true;
  };

  const handleChangeDate = (date) => {
    setDeliveryDateStr(date);
  };

  const handleChangeTime = (_, timeString) => {
    setDeliveryTimeStr(timeString);
  };

  const handleClickSave = () => {
    if (validate()) {
      dispatch(
        callThunk({
          action: updateSurvey,
          payload: { deliveryDate: formatISO(new Date(`${deliveryDateStr} ${deliveryTimeStr}`)) },
        })
      );
      handleClose();
    }
  };

  /**
   * =============
   * hooks
   * =============
   */

  useEffect(() => {
    const extractedDate = extractDateFromIsoDate(isoDeliveryDate);
    setDeliveryDateStr(`${extractedDate.year}-${extractedDate.month}-${extractedDate.dt}`);
    setDeliveryTimeStr(`${extractedDate.hour}:${extractedDate.minute}`);
    setDefExtractedDate(extractedDate);
  }, [isoDeliveryDate]);

  useEffect(() => {
    const extractedDate = extractDateFromIsoDate(minDeliveryDate);
    setMinDateStr(`${extractedDate.year}-${extractedDate.month}-${extractedDate.dt}`);
  }, [minDeliveryDate]);

  /**
   * datepickerで時間を変更してOKボタンを押したタイミングでもバリデーション発火
   */
  useEffect(() => {
    validate();
  }, [deliveryDateStr]);

  /**
   * timepickerで時間を変更してOKボタンを押したタイミングでもバリデーション発火
   */
  useEffect(() => {
    validate();
  }, [deliveryTimeStr]);

  return (
    <BasicModal
      open={open}
      titleProps={{
        titleLabelVariant: 'body1',
        titleLabel: t('quotas.modals.deliveryDate.titleLabel'),
        desc: t('quotas.modals.deliveryDate.desc'),
      }}
      handleClose={() => {
        handleClose();
        // 選択状態を元に戻してあげる
        setDeliveryDateStr(
          `${defExtractedDate.year}-${defExtractedDate.month}-${defExtractedDate.dt}`
        );
        setDeliveryTimeStr(`${defExtractedDate.hour}:${defExtractedDate.minute}`);
      }}
      buttons={[<SaveButton key="button1" handleClick={handleClickSave} sx={{ width: 148 }} />]}
      dataTestId="DeliveryDateModal"
      width="33%"
    >
      <BodyWrapper>
        <DatePickerWrapper>
          <DatePicker
            label={t('survey.deliveryDate')}
            date={deliveryDateStr}
            minDate={minDateStr}
            maxDate={addDaystoIsoDate(minDateStr, 30)} // 上限値は30日
            forbiddenSchedules={forbiddenSchedules}
            handleChange={handleChangeDate}
          />
        </DatePickerWrapper>

        <TimePickerWrapper>
          <TimePicker
            label={t('survey.deliveryTime')}
            defTime={deliveryTimeStr}
            handleChange={handleChangeTime}
            error={errors.invalid}
          />
        </TimePickerWrapper>
        {errors.invalid && (
          <InputValidationErrorBox
            message={errors.message}
            dataTestId="DeliveryDateModal-TimePicker"
            position="center"
          />
        )}
      </BodyWrapper>
    </BasicModal>
  );
}

DeliveryDateModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  isoDeliveryDate: propTypesNullable(PropTypes.string.isRequired),
  minDeliveryDate: propTypesNullable(PropTypes.string.isRequired),
  forbiddenSchedules: PropTypes.arrayOf(
    PropTypes.shape({
      startTime: PropTypes.instanceOf(Date),
      endTime: PropTypes.instanceOf(Date),
    })
  ),
};

export default DeliveryDateModal;
